import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  LinearProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import axiosInstance from '../../utils/axiosConfig';

interface BulkImportRow {
  productName: string;
  barcode: string;
  operation: 'add' | 'remove' | 'remove_default';
  isDefaultCategory: boolean;
  rowNumber: number;
}

interface ProcessingStatus {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  warnings: number;
  currentBatch: number;
  totalBatches: number;
  isRunning: boolean;
  isPaused: boolean;
  errors: Array<{
    rowNumber: number;
    barcode: string;
    productName: string;
    error: string;
  }>;
}

interface CategoryBulkExcelImportProps {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  onImportComplete: () => void;
}

const CategoryBulkExcelImport: React.FC<CategoryBulkExcelImportProps> = ({
  open,
  onClose,
  categoryId,
  categoryName,
  onImportComplete
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<BulkImportRow[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [processing, setProcessing] = useState<ProcessingStatus>({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    warnings: 0,
    currentBatch: 0,
    totalBatches: 0,
    isRunning: false,
    isPaused: false,
    errors: []
  });
  const [jobId, setJobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const steps = ['رفع الملف', 'معاينة البيانات', 'المعالجة', 'النتائج'];
  const BATCH_SIZE = 200; // حجم المجموعة الواحدة

  // إعادة تعيين الحالة
  const resetState = () => {
    // إيقاف المراقبة أولاً
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
      processingIntervalRef.current = null;
    }
    
    setFile(null);
    setExcelData([]);
    setActiveStep(0);
    setJobId(null);
    setProcessing({
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      warnings: 0,
      currentBatch: 0,
      totalBatches: 0,
      isRunning: false,
      isPaused: false,
      errors: []
    });
  };

  const handleClose = () => {
    // إذا كانت المعالجة جارية، تأكيد الإغلاق
    if (processing.isRunning && activeStep === 2) {
      const confirmClose = window.confirm(
        'المعالجة جارية حالياً. هل تريد حقاً إغلاق النافذة؟ ستستمر المعالجة في الخلفية.'
      );
      if (!confirmClose) {
        return;
      }
    }
    
    resetState();
    onClose();
  };

  // تنظيف عند إلغاء تحميل المكون
  React.useEffect(() => {
    return () => {
      if (processingIntervalRef.current) {
        clearInterval(processingIntervalRef.current);
        processingIntervalRef.current = null;
      }
    };
  }, []);

  // تحميل قالب الإكسل المحسن
  const downloadTemplate = () => {
    const templateData = [
      {
        'اسم المنتج': 'مثال منتج 1',
        'الباركود': '1234567890123',
        'العملية': 'add',
        'تصنيف افتراضي': 'yes'
      },
      {
        'اسم المنتج': 'مثال منتج 2',
        'الباركود': '9876543210987', 
        'العملية': 'remove',
        'تصنيف افتراضي': 'no'
      },
      {
        'اسم المنتج': 'مثال منتج 3',
        'الباركود': '5555555555555',
        'العملية': 'remove_default',
        'تصنيف افتراضي': 'no'
      }
    ];

    // إضافة تعليمات في ورقة منفصلة
    const instructionsData = [
      { 'العمود': 'اسم المنتج', 'الوصف': 'اسم المنتج (اختياري للمرجع)', 'مثال': 'قلم أزرق' },
      { 'العمود': 'الباركود', 'الوصف': 'باركود المتغير (مطلوب)', 'مثال': '1234567890123' },
      { 'العمود': 'العملية', 'الوصف': 'add: إضافة، remove: حذف، remove_default: حذف افتراضي', 'مثال': 'add' },
      { 'العمود': 'تصنيف افتراضي', 'الوصف': 'yes أو نعم لتعيين كافتراضي، no أو لا لعدم التعيين', 'مثال': 'yes' }
    ];

    const wb = XLSX.utils.book_new();
    
    // ورقة البيانات
    const ws1 = XLSX.utils.json_to_sheet(templateData);
    ws1['!cols'] = [
      { wch: 25 }, // اسم المنتج
      { wch: 20 }, // الباركود
      { wch: 15 }, // العملية
      { wch: 15 }  // تصنيف افتراضي
    ];
    XLSX.utils.book_append_sheet(wb, ws1, 'البيانات');
    
    // ورقة التعليمات
    const ws2 = XLSX.utils.json_to_sheet(instructionsData);
    ws2['!cols'] = [
      { wch: 15 }, // العمود
      { wch: 50 }, // الوصف
      { wch: 20 }  // مثال
    ];
    XLSX.utils.book_append_sheet(wb, ws2, 'التعليمات');

    XLSX.writeFile(wb, `قالب_استيراد_مجمع_${categoryName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // معالجة رفع الملف
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      parseExcelFile(uploadedFile);
    }
  };

  // تحليل ملف الإكسل
  const parseExcelFile = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      // البحث عن ورقة البيانات
      let sheetName = workbook.SheetNames.find(name => 
        name.includes('البيانات') || name.includes('Data') || name === workbook.SheetNames[0]
      ) || workbook.SheetNames[0];
      
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const parsedData: BulkImportRow[] = jsonData
        .map((row: any, index: number) => {
          const productName = row['اسم المنتج'] || row['Product Name'] || '';
          const barcode = row['الباركود'] || row['Barcode'] || '';
          const operation = row['العملية'] || row['Operation'] || 'add';
          const isDefaultStr = row['تصنيف افتراضي'] || row['Default Category'] || 'no';
          
          return {
            productName: String(productName).trim(),
            barcode: String(barcode).trim(),
            operation: operation.toLowerCase() as 'add' | 'remove' | 'remove_default',
            isDefaultCategory: ['yes', 'نعم', 'true', '1'].includes(isDefaultStr.toLowerCase()),
            rowNumber: index + 2
          };
        })
        .filter(row => row.barcode); // تصفية الصفوف الفارغة

      setExcelData(parsedData);
      setProcessing(prev => ({
        ...prev,
        total: parsedData.length,
        totalBatches: Math.ceil(parsedData.length / BATCH_SIZE)
      }));
      setActiveStep(1);
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      alert('حدث خطأ في قراءة ملف الإكسل. تأكد من صحة تنسيق الملف.');
    }
  };

  // بدء المعالجة المجمعة
  const startBulkProcessing = async () => {
    try {
      setActiveStep(2);
      setProcessing(prev => ({ ...prev, isRunning: true, isPaused: false }));
      
      // إرسال البيانات للخادم لبدء المعالجة في الخلفية
      const response = await axiosInstance.post(
        `/categories/${categoryId}/bulk-excel-import`,
        {
          data: excelData,
          batchSize: BATCH_SIZE,
          categoryName
        },
        {
          timeout: 60000
        }
      );

      const result = response.data;
      
      if (!result.success) {
        throw new Error(result.message || 'فشل في بدء المعالجة');
      }
      
      if (!result.jobId) {
        throw new Error('لم يتم إرجاع معرف المهمة من الخادم');
      }
      
      setJobId(result.jobId);

      // بدء مراقبة التقدم
      setTimeout(() => {
        startProgressMonitoring(result.jobId);
      }, 500);
    } catch (error: any) {
      console.error('Error starting bulk processing:', error);
      const errorMessage = error.response?.data?.message || error.message || 'حدث خطأ أثناء بدء المعالجة';
      alert(errorMessage);
      setProcessing(prev => ({ ...prev, isRunning: false }));
      setActiveStep(1); // العودة لخطوة المعاينة
    }
  };

  // مراقبة تقدم المعالجة
  const startProgressMonitoring = (jobId: string) => {
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;
    let checkCount = 0;
    
    processingIntervalRef.current = setInterval(async () => {
      checkCount++;
      try {
        const response = await axiosInstance.get(
          `/categories/bulk-import-status/${jobId}?_t=${Date.now()}&_c=${checkCount}`,
          {
            timeout: 15000,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          }
        );
        
        const status = response.data;
        consecutiveErrors = 0;
        
        if (!status.success) {
          if (processingIntervalRef.current) {
            clearInterval(processingIntervalRef.current);
            processingIntervalRef.current = null;
          }
          alert('حدث خطأ أثناء مراقبة التقدم: ' + status.message);
          setProcessing(prev => ({ ...prev, isRunning: false }));
          return;
        }
        
        setProcessing(prev => ({
          ...prev,
          processed: status.processed || 0,
          successful: status.successful || 0,
          failed: status.failed || 0,
          warnings: status.warnings || 0,
          currentBatch: status.currentBatch || 0,
          errors: status.errors || [],
          isRunning: status.isRunning === true
        }));

        if (status.isRunning === false || status.processed >= status.total) {
          if (processingIntervalRef.current) {
            clearInterval(processingIntervalRef.current);
            processingIntervalRef.current = null;
          }
          
          setProcessing(prev => ({
            ...prev,
            processed: status.processed || prev.total,
            successful: status.successful || 0,
            failed: status.failed || 0,
            warnings: status.warnings || 0,
            errors: status.errors || [],
            isRunning: false,
            isPaused: false
          }));
          
          setActiveStep(3);
          onImportComplete();
        }
      } catch (error: any) {
        consecutiveErrors++;
        
        if (consecutiveErrors >= maxConsecutiveErrors) {
          if (processingIntervalRef.current) {
            clearInterval(processingIntervalRef.current);
            processingIntervalRef.current = null;
          }
          
          const shouldContinue = window.confirm(
            'فقد الاتصال بالخادم أثناء مراقبة التقدم.\n' +
            'قد تكون المعالجة قد اكتملت في الخلفية.\n\n' +
            'هل تريد الانتقال إلى صفحة النتائج؟'
          );
          
          if (shouldContinue) {
            setProcessing(prev => ({ ...prev, isRunning: false }));
            setActiveStep(3);
          } else {
            setProcessing(prev => ({ ...prev, isRunning: false }));
            setActiveStep(1);
          }
        } else if (error.response?.status === 404) {
          if (processingIntervalRef.current) {
            clearInterval(processingIntervalRef.current);
            processingIntervalRef.current = null;
          }
          alert('لم يتم العثور على المهمة. قد تكون انتهت بالفعل.');
          setProcessing(prev => ({ ...prev, isRunning: false }));
          setActiveStep(3);
        }
      }
    }, 2000);
  };

  // إيقاف المعالجة مؤقتاً
  const pauseProcessing = async () => {
    if (jobId) {
      try {
        await axiosInstance.post(`/categories/bulk-import-pause/${jobId}`);
        setProcessing(prev => ({ ...prev, isPaused: true }));
      } catch (error) {
        console.error('Error pausing processing:', error);
      }
    }
  };

  // استئناف المعالجة
  const resumeProcessing = async () => {
    if (jobId) {
      try {
        await axiosInstance.post(`/categories/bulk-import-resume/${jobId}`);
        setProcessing(prev => ({ ...prev, isPaused: false }));
      } catch (error) {
        console.error('Error resuming processing:', error);
      }
    }
  };

  // إلغاء المعالجة
  const cancelProcessing = async () => {
    if (jobId) {
      try {
        await axiosInstance.post(`/categories/bulk-import-cancel/${jobId}`);
        if (processingIntervalRef.current) {
          clearInterval(processingIntervalRef.current);
        }
        setProcessing(prev => ({ ...prev, isRunning: false, isPaused: false }));
      } catch (error) {
        console.error('Error canceling processing:', error);
      }
    }
  };

  // حساب نسبة التقدم
  const getProgress = () => {
    if (processing.total === 0) return 0;
    return (processing.processed / processing.total) * 100;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            استيراد مجمع للمنتجات - {categoryName}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* مؤشر الخطوات */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* الخطوة الأولى: رفع الملف */}
        {activeStep === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>نظام الاستيراد المجمع يدعم:</strong>
                <br />• معالجة آلاف المنتجات بكفاءة عالية
                <br />• معالجة في الخلفية مع إمكانية الإيقاف والاستئناف
                <br />• تقارير مفصلة عن النجاح والأخطاء
                <br />• معالجة مجمعة لتجنب انقطاع الاتصال
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      تحميل قالب الإكسل
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      احصل على قالب جاهز مع التعليمات
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={downloadTemplate}
                      fullWidth
                    >
                      تحميل القالب
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                      رفع ملف الإكسل
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      اختر ملف الإكسل المحتوي على البيانات
                    </Typography>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                    />
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      fullWidth
                    >
                      اختيار الملف
                    </Button>
                    {file && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        الملف المحدد: {file.name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* الخطوة الثانية: معاينة البيانات */}
        {activeStep === 1 && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              تم تحليل الملف بنجاح! تم العثور على {excelData.length} عنصر للمعالجة
            </Alert>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {excelData.length}
                  </Typography>
                  <Typography variant="body2">
                    إجمالي العناصر
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {excelData.filter(item => item.operation === 'add').length}
                  </Typography>
                  <Typography variant="body2">
                    للإضافة
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {excelData.filter(item => item.operation === 'remove').length}
                  </Typography>
                  <Typography variant="body2">
                    للحذف
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">
                    {excelData.filter(item => item.operation === 'remove_default').length}
                  </Typography>
                  <Typography variant="body2">
                    حذف افتراضي
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              سيتم معالجة البيانات على {Math.ceil(excelData.length / BATCH_SIZE)} مجموعة، 
              كل مجموعة تحتوي على {BATCH_SIZE} عنصر كحد أقصى
            </Typography>
          </Box>
        )}

        {/* الخطوة الثالثة: المعالجة */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              جاري المعالجة...
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={getProgress()} 
              sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />
            
            <Typography variant="body2" align="center" sx={{ mb: 3 }}>
              {processing.processed} من {processing.total} ({Math.round(getProgress())}%)
              <br />
              المجموعة {processing.currentBatch} من {processing.totalBatches}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                  <Typography variant="h5" color="success.contrastText">
                    {processing.successful}
                  </Typography>
                  <Typography variant="body2" color="success.contrastText">
                    نجح
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                  <Typography variant="h5" color="error.contrastText">
                    {processing.failed}
                  </Typography>
                  <Typography variant="body2" color="error.contrastText">
                    فشل
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                  <Typography variant="h5" color="warning.contrastText">
                    {processing.warnings}
                  </Typography>
                  <Typography variant="body2" color="warning.contrastText">
                    تحذيرات
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                  <Typography variant="h5" color="info.contrastText">
                    {processing.total - processing.processed}
                  </Typography>
                  <Typography variant="body2" color="info.contrastText">
                    متبقي
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* أزرار التحكم */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              {processing.isRunning && !processing.isPaused && (
                <Button
                  variant="outlined"
                  startIcon={<PauseIcon />}
                  onClick={pauseProcessing}
                >
                  إيقاف مؤقت
                </Button>
              )}
              
              {processing.isPaused && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={resumeProcessing}
                >
                  استئناف
                </Button>
              )}
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={cancelProcessing}
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        )}

        {/* الخطوة الرابعة: النتائج */}
        {activeStep === 3 && (
          <Box>
            <Alert 
              severity={processing.failed > 0 ? "warning" : "success"} 
              sx={{ mb: 3 }}
            >
              <Typography variant="body1">
                تمت المعالجة! نجح: {processing.successful}، فشل: {processing.failed}، تحذيرات: {processing.warnings}
              </Typography>
            </Alert>

            {processing.errors.length > 0 && (
              <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  الأخطاء والتحذيرات:
                </Typography>
                <List dense>
                  {processing.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`الصف ${error.rowNumber}: ${error.productName || error.barcode}`}
                        secondary={error.error}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {activeStep === 3 ? 'إغلاق' : 'إلغاء'}
        </Button>
        
        {activeStep === 1 && (
          <Button 
            onClick={startBulkProcessing} 
            variant="contained" 
            color="primary"
            disabled={excelData.length === 0}
          >
            بدء المعالجة ({excelData.length} عنصر)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CategoryBulkExcelImport;